import type { CharacterView, LocalizedText, LogEntry, StoryLanguage } from "./contract";

// The story as the player reads it (O3, contract §3.5): the openings, the player's turns with their narrations, and
// the closing, one text without a scene heading, divider or label. Narration is untrusted plain text: it is split
// into paragraphs here and rendered as text nodes, never as markup. A narration keeps the id of the run that told it,
// so the page can find the words of an item that run brought. A turn's pole is shown as the log
// stored it: spark sends none any more, but a turn sent before may carry one. A player item keeps the turn's number
// as the log stored it, counted over the whole game, so a log cut to its last turns still numbers them right.

export type StoryItem =
  | { kind: "narration"; key: string; runId: string; text: string }
  | { kind: "player"; key: string; turn: number; pole: string | null; text: string };

export function storyItems(entries: readonly LogEntry[], language: StoryLanguage): StoryItem[] {
  return entries.flatMap((entry): StoryItem[] => {
    switch (entry.type) {
      case "opening":
      case "closing":
        return [{ kind: "narration", key: `${entry.type}:${entry.runId}`, runId: entry.runId, text: entry.text }];
      case "turn":
        return [
          {
            kind: "player",
            key: `input:${entry.runId}`,
            turn: entry.turn,
            pole: entry.input.pole ? entry.input.pole.text[language] : null,
            text: entry.input.text,
          },
          { kind: "narration", key: `turn:${entry.runId}`, runId: entry.runId, text: entry.narration },
        ];
      case "scene_end":
        // Data for spark, never a visible break.
        return [];
    }
  });
}

/**
 * A piece of a story text: as told, or marked as the word of an item the player has just got or of a character who has
 * just come into the story (82).
 */
export interface StoryPart {
  text: string;
  marked: boolean;
}

/**
 * A word to mark in a story text (82): the name of an item the player has just got or of a character
 * who has just come into the story, or, while such a character's name is not known, their description's head noun.
 * `whole`: it marks only as a whole word, never with an ending, as a head noun does.
 */
export interface StoryMark {
  word: string;
  whole: boolean;
}

// What a word is made of in any language: letters, digits and combining marks.
const WORD = "\\p{L}\\p{N}\\p{M}";
const WORD_CHAR = new RegExp(`[${WORD}]`, "u");
const LEADING_WORD_CHAR = new RegExp(`^[${WORD}]`, "u");
const TRAILING_WORD_CHAR = new RegExp(`[${WORD}]$`, "u");
const NON_WORD_RUN = new RegExp(`[^${WORD}]+`, "u");

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wordChars(text: string): number {
  return Array.from(text).filter((char) => WORD_CHAR.test(char)).length;
}

// How a name can show in a story text: the whole name, and for a name of several words also its last word, the noun it
// ends in ("Belegtes Brötchen" is told as "das Brötchen"), though only a last word of four letters or more, since a
// shorter one finds too much on its own ("alt" of "Handy (alt)" in "Alternative"). Either may take an ending of up to
// three letters, so an inflected form is found too ("Ohrstöpsel" finds "Ohrstöpseln"), but never a longer word that only
// starts with it ("Wasser" in "Wasserflasche", "Karte" in "Kartenspieler"). A name of fewer than three letters only finds
// itself as a whole word: with an ending it would find far too much ("Ei" in "Eimer"). So does a head noun (`whole`),
// the one word a description names its person by: "Mann" never finds "Mannschaft", nor "Flugbegleiterin" the plural
// "Flugbegleiterinnen". A name that ends in a sign rather than a letter ends there.
function markPatterns({ word, whole }: StoryMark): string[] {
  const name = word.trim();
  if (wordChars(name) === 0) return [];
  const words = name.split(NON_WORD_RUN).filter((part) => part !== "");
  const last = words[words.length - 1];
  const candidates = words.length > 1 && last !== undefined && wordChars(last) >= 4 ? [name, last] : [name];
  return candidates.map((candidate) => {
    const pattern = candidate.split(/\s+/u).map(escapeRegExp).join("\\s+");
    if (!TRAILING_WORD_CHAR.test(candidate)) return pattern;
    return !whole && wordChars(candidate) >= 3 ? `${pattern}[${WORD}]{0,3}(?![${WORD}])` : `${pattern}(?![${WORD}])`;
  });
}

/**
 * The text cut where it names one of `marks`: at the start of a word, in any case, a name with an ending of up to three
 * letters and a head noun only as the word itself. Plain text in, plain text out: the caller renders every part as
 * text, a marked one inside a mark element. A text that names none of them comes back whole, as one part.
 */
export function markedParts(text: string, marks: readonly StoryMark[]): StoryPart[] {
  const patterns = [...new Set(marks.flatMap(markPatterns))].sort((a, b) => b.length - a.length);
  if (patterns.length === 0) return [{ text, marked: false }];
  const find = new RegExp(patterns.join("|"), "giu");
  const parts: StoryPart[] = [];
  let at = 0;
  for (let match = find.exec(text); match !== null; match = find.exec(text)) {
    const start = match.index;
    // Inside a word is no start of one: "Brötchen" does not mark the end of "Käsebrötchen". The letter before may take
    // two UTF-16 units.
    if (LEADING_WORD_CHAR.test(match[0]) && TRAILING_WORD_CHAR.test(text.slice(Math.max(0, start - 2), start))) {
      find.lastIndex = start + 1;
      continue;
    }
    if (start > at) parts.push({ text: text.slice(at, start), marked: false });
    parts.push({ text: match[0], marked: true });
    at = start + match[0].length;
  }
  if (at < text.length || parts.length === 0) parts.push({ text: text.slice(at), marked: false });
  return parts;
}

// The words a character's description goes on with once it has named them (82): where they are, what they have or whose
// they are ("die Frau mit dem Baby", "the man from row 12"), never who they are. A number goes on too ("zwei Reihen").
const GOES_ON: Record<StoryLanguage, ReadonlySet<string>> = {
  de: new Set([
    "mit", "ohne", "aus", "von", "vom", "bei", "beim", "neben", "am", "an", "im", "in", "auf", "hinter", "vor", "über",
    "unter", "zwischen", "gegenüber", "für", "zu", "zum", "zur", "nach", "seit", "durch", "um", "wie", "als", "und",
    "oder", "der", "die", "das", "den", "dem", "des", "ein", "eine", "einer", "eines", "einem", "einen", "welcher",
    "welche", "welches", "dessen", "deren", "dort", "da", "hier", "zwei", "drei", "vier", "fünf", "sechs", "sieben",
    "acht", "neun", "zehn",
  ]),
  en: new Set([
    "with", "without", "from", "next", "near", "in", "on", "at", "by", "beside", "behind", "before", "after", "of",
    "for", "to", "into", "across", "around", "under", "over", "above", "below", "between", "opposite", "who", "whose",
    "whom", "that", "which", "and", "or", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  ]),
};

// The articles a description opens with: they never name anyone.
const ARTICLES: Record<StoryLanguage, ReadonlySet<string>> = {
  de: new Set(["der", "die", "das", "den", "dem", "des", "ein", "eine", "einen", "einem", "einer", "eines"]),
  en: new Set(["the", "a", "an"]),
};

const DIGIT = /^\p{N}/u;

/**
 * The word a character's description names them by, its head noun (82): "Frau" in "die Frau mit dem Baby", "man" in
 * "the man from row 12". It is the last word before the description goes on with where they are or what they have; in
 * German, which writes its nouns with a capital letter, the last such word there ("der schlafende Mann"). Null when no
 * word of it names anyone.
 */
export function headNoun(description: string, language: StoryLanguage): string | null {
  const words = description.split(NON_WORD_RUN).filter((word) => word !== "");
  const plain = (word: string) => word.toLocaleLowerCase(language);
  const end = words.findIndex((word, index) => index > 0 && (GOES_ON[language].has(plain(word)) || DIGIT.test(word)));
  const naming = (end < 0 ? words : words.slice(0, end)).filter((word) => !ARTICLES[language].has(plain(word)));
  const nouns = language === "de" ? naming.filter((word) => word !== plain(word)) : [];
  return (nouns.length > 0 ? nouns : naming).at(-1) ?? null;
}

/** How the page names a character: by the name fact once it is known, else by the description as authored (§3.5). */
export function characterName(character: CharacterView, language: StoryLanguage): string {
  const name = character.facts.find((fact) => fact.kind === "name");
  return name ? name.value[language] : character.placeholder[language];
}

/**
 * The word the narration that brought a character names them by (82): their name once it is known, else their
 * description's head noun, in each language, which marks only as a whole word (`whole`). Empty where nothing names
 * them, which marks nothing.
 */
export function characterWord(character: CharacterView): { word: LocalizedText; whole: boolean } {
  const name = character.facts.find((fact) => fact.kind === "name");
  if (name) return { word: { de: name.value.de, en: name.value.en }, whole: false };
  const noun = (language: StoryLanguage) => headNoun(character.placeholder[language], language) ?? "";
  return { word: { de: noun("de"), en: noun("en") }, whole: true };
}

/**
 * The text starting with a capital letter, as the story's language writes it, and otherwise as written: a character's
 * description is authored to be told mid-sentence ("der Mann aus Reihe 12") and becomes a heading only where the page
 * names the character by it.
 */
export function capitalized(text: string, language: StoryLanguage): string {
  const first = text.codePointAt(0);
  if (first === undefined) return text;
  const head = String.fromCodePoint(first);
  return head.toLocaleUpperCase(language) + text.slice(head.length);
}

/** Paragraphs split on blank lines (§3.1); a single line break stays inside its paragraph. */
export function paragraphs(text: string): string[] {
  return text
    .split(/\n[ \t]*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== "");
}

const STORY_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/**
 * The story's clock (38) is a wall clock without a zone: its five numbers as a UTC instant, to be formatted with
 * `timeZone: "UTC"` so the browser's zone never shifts it.
 */
export function storyClock(value: string): Date | null {
  const match = STORY_TIME.exec(value);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  return new Date(Date.UTC(year, month - 1, day, hour, minute));
}

/** Unicode code points, counted as ajv and agent-service count a turn's text (§5.3). */
export function codePoints(text: string): number {
  return Array.from(text).length;
}

/** U+0000 is the one character agent-service refuses in a turn (§4.1); it cannot be typed, only pasted. */
export function turnText(text: string): string {
  return text.replaceAll("\u0000", "");
}

/**
 * Whether a turn's text holds no words (96: a move needs words): nothing but
 * whitespace, as JavaScript's `trim` counts it or as agent-service's Kotlin `isBlank` does, which also counts U+001C to
 * U+001F. By either count, so spark never sends what the BFF or agent-service refuses as `text_required`.
 */
export function isBlankText(text: string): boolean {
  return Array.from(text).every((char) => char.trim() === "" || (char >= "\u001C" && char <= "\u001F"));
}
